import { useState } from 'react'
import { ChatBubbleLeftEllipsisIcon, XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import { useAuth } from '../../hooks/useAuth'
import { submitFeedback } from '../../services/feedbackService'

type Props = {
  isOpen: boolean
  onClose: () => void
}

export const FeedbackModal = ({ isOpen, onClose }: Props) => {
  const { sessionUserId } = useAuth()
  const [message, setMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  if (!isOpen) return null

  const handleSubmit = async () => {
    if (!message.trim() || !sessionUserId) return
    setIsSubmitting(true)
    try {
      await submitFeedback(sessionUserId, message.trim())
      setIsSuccess(true)
      setTimeout(() => {
        setMessage('')
        setIsSuccess(false)
        onClose()
      }, 1500)
    } catch {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (isSubmitting) return
    setMessage('')
    setIsSuccess(false)
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="w-full max-w-md border border-app-line bg-app-surface p-4 space-y-3 rounded-2xl shadow-app-card">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-app-ink flex items-center gap-2">
            <ChatBubbleLeftEllipsisIcon className="h-4 w-4" aria-hidden="true" /> Send Feedback
          </p>
          <button
            onClick={handleClose}
            className="text-app-muted hover:text-app-ink"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {isSuccess ? (
          <div className="flex flex-col items-center gap-2 py-6">
            <CheckCircleIcon className="h-10 w-10 text-emerald-500" />
            <p className="text-sm font-medium text-app-ink">Thanks for your feedback!</p>
          </div>
        ) : (
          <>
            <textarea
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full rounded-lg border border-app-line bg-app-surface px-3 py-2 text-sm text-app-ink focus:border-app-accent focus:outline-none"
              placeholder="What could we improve?"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={handleClose}
                className="rounded-lg border border-app-line px-3 py-2 text-sm font-semibold text-app-ink hover:bg-app-subtle"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!message.trim() || isSubmitting}
                className="rounded-lg bg-app-primary px-3 py-2 text-sm font-semibold text-white hover:bg-app-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Sending...' : 'Submit'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
